import {IBusiness} from 'app/modules/discovery/core'
import React, {useState} from 'react'

export interface ParsedCV {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  employer: IBusiness[]
}

export interface StarterInfo {
  first_name: string
  last_name: string
  email: string
  phoneNumber?: string
  orgName?: string
  employer?: IBusiness
}

export enum InviteUserSteps {
  Welcome = 1,
  EnterBasicInfo = 2,
  AwaitLogin = 3,
  FindTenant = 4,
  ProjectInvitation = 5,
}

export const TOTAL_STEPS = Math.floor(Object.keys(InviteUserSteps).length / 2)

interface InviteProviderContextState {
  modalOpen: boolean
  setModalOpen: (flag: boolean) => void
  step: InviteUserSteps
  totalSteps: number
  setStep: (step: InviteUserSteps) => void
  goToNextStep: () => void
  goToPrevStep: () => void
  starterInfo: StarterInfo
  setStarterData: (si: StarterInfo) => void
}

const initialState: InviteProviderContextState = {
  modalOpen: true,
  setModalOpen: () => {},
  step: InviteUserSteps.Welcome,
  totalSteps: TOTAL_STEPS,
  setStep: () => {},
  goToNextStep: () => {},
  goToPrevStep: () => {},
  starterInfo: {
    first_name: '',
    last_name: '',
    phoneNumber: '',
    email: '',
    orgName: '',
    employer: undefined,
  },
  setStarterData: () => {},
}

const InviteProviderContext = React.createContext<InviteProviderContextState>(initialState)

export default function SignupContextProvider({children}: {children: React.ReactNode}) {
  const [starterData, setStarterData] = useState<StarterInfo>(initialState.starterInfo)
  const [showModal, setShowModal] = React.useState<boolean>(initialState.modalOpen)
  const [modalStep, setModalStep] = React.useState(initialState.step)

  const setStep = React.useCallback((step: number) => {
    if (step < 1) {
      return setModalStep(1)
    }
    if (step > TOTAL_STEPS) {
      return setShowModal(false)
    }
    setModalStep(step)
  }, [])

  const setModalOpen = React.useCallback((flag: boolean) => {
    setShowModal(flag)
  }, [])

  const goToNextStep = React.useCallback(() => {
    setStep(modalStep + 1)
  }, [modalStep, setStep])
  const goToPrevStep = React.useCallback(() => {
    setStep(modalStep - 1)
  }, [modalStep, setStep])
  const completeCreating = React.useCallback(() => {
    setShowModal(false)
  }, [])

  return (
    <InviteProviderContext.Provider
      value={{
        modalOpen: showModal,
        setModalOpen: setModalOpen,
        totalSteps: TOTAL_STEPS,
        step: modalStep,
        setStep,
        goToNextStep,
        goToPrevStep,
        starterInfo: starterData,
        setStarterData,
      }}
    >
      {children}
    </InviteProviderContext.Provider>
  )
}

export const useInviteProvider = (): InviteProviderContextState =>
  React.useContext(InviteProviderContext)
